import type { Messages } from '../catalogue.en'

/**
 * The external clinical entry: care recorded from outside the clinic, its
 * dedicated page, lifecycle, translations, and the entries an AI processing
 * proposes from patient documents.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const externalEntry: Partial<Messages> = {
  "externalEntry.approximateDate": "Примерно {date}",
  "externalEntry.recordedBy": "Внёс запись",
  "externalEntry.title": "Внешняя медицинская запись",
  "externalEntry.external": "Внешняя",
  "externalEntry.statusAmending": "Изменяется",
  "externalEntry.careDetails": "Сведения о помощи",
  "externalEntry.place": "Место",
  "externalEntry.eventDate": "Дата оказания помощи",
  "externalEntry.dateHint": "Укажите год, год и месяц или полную дату. Год и месяц отображаются как приблизительная дата.",
  "externalEntry.approximate": "Примерно",
  "externalEntry.approximateMark": "≈",
  "externalEntry.heading": "Заголовок",
  "externalEntry.diagnoses": "Диагнозы",
  "externalEntry.diagnosisHint": "Необязательно. Каждый диагноз — код из словаря или формулировка источника. Код не подтверждает диагноз клинически; эти диагнозы остаются во внешней записи.",
  "externalEntry.diagnosisReadHint": "Сопоставление кода не означает клиническое подтверждение. Эти диагнозы остаются во внешней записи.",
  "externalEntry.diagnosesEmpty": "Диагнозы не указаны.",
  "externalEntry.addDiagnosis": "Добавить диагноз",
  "externalEntry.diagnosisAddHint": "Выберите коды в панели «Словарь диагнозов» или впишите текстом диагноз, которого в словаре нет.",
  "externalEntry.newDiagnosisText": "Диагноз текстом",
  "externalEntry.addDiagnosisText": "Добавить текстом",
  "externalEntry.diagnosisText": "Текст диагноза",
  "externalEntry.diagnosisAsText": "Текст",
  "externalEntry.done": "Готово",
  "externalEntry.diagnosisUnsavedIntent": "Внешняя медицинская запись — диагноз введён, но не добавлен",
  "externalEntry.doctor": "Внешний врач",
  "externalEntry.clinic": "Внешняя клиника",
  "externalEntry.findOrAddName": "Найдите или добавьте",
  "externalEntry.addName": "Добавить «{name}»",
  "externalEntry.newNameHint": "Новое имя: появится в справочнике при сохранении записи.",
  "externalEntry.manageName": "Переименовать или объединить в справочнике",
  "externalEntry.globalNameHint": "Изменение отразится во всех текущих записях с этим именем. В сохранённой истории останется прежнее название.",
  "externalEntry.rename": "Переименовать",
  "externalEntry.mergeInto": "Объединить с этим именем",
  "externalEntry.merge": "Объединить",
  "externalEntry.country": "Страна",
  "externalEntry.city": "Город",
  "externalEntry.source": "Источник сведений",
  "externalEntry.files": "Файлы пациента, связанные с записью",
  "externalEntry.filesEmpty": "К записи не привязаны файлы.",
  "externalEntry.aiPages": "ИИ: стр. {pages}",
  "externalEntry.aiWhole": "ИИ: весь файл",
  "externalEntry.viewFile": "Открыть файл",
  "externalEntry.linkFile": "Привязать файл пациента",
  "externalEntry.chooseFile": "Выберите файл",
  "externalEntry.link": "Привязать",
  "externalEntry.unlink": "Отвязать",
  "externalEntry.unlinkFile": "Отвязать {name}",
  "externalEntry.upload": "Загрузить и связать файл",
  "externalEntry.uploading": "Загрузка…",
  "externalEntry.uploadFailed": "Не удалось загрузить файл.",
  "externalEntry.filesSavedWithEntry": "Привязка файлов сохраняется вместе с записью. Загруженный файл сразу появляется в файлах пациента.",
  "externalEntry.filesLocked": "Привязка файлов меняется только в черновике или при исправлении записи.",
  "externalEntry.remove": "Убрать",
  "externalEntry.saving": "Сохранение…",
  "externalEntry.required": "Заполните текст записи и хотя бы год оказания помощи.",
  "externalEntry.loadFailed": "Не удалось загрузить внешнюю запись.",
  "externalEntry.writeFailed": "Не удалось сохранить. Проверьте поля и права. При конфликте загрузите текущую запись и проверьте свой черновик.",
  "externalEntry.writeForbidden": "Недостаточно прав для изменения этой записи.",
  "externalEntry.notEditable": "В текущем состоянии запись больше нельзя изменить. Обновите страницу, чтобы увидеть её актуальный вид.",
  "externalEntry.conflict": "Запись была изменена в другом месте. Загрузите сохранённую версию, чтобы продолжить; несохранённые правки будут отброшены.",
  "externalEntry.changedElsewhere": "Запись была изменена в другом месте. Загружена текущая версия — проверьте её и повторите.",
  "externalEntry.unsavedIntent": "Внешняя медицинская запись — правки, которые не были сохранены",
  "externalEntry.translationUnsavedIntent": "Перевод внешней медицинской записи — правки, которые не были сохранены",
  "externalEntry.completedNotice": "Внешняя запись завершена и доступна только для чтения. Чтобы изменить её, начните исправление.",
  "externalEntry.completeConfirmBody": "После завершения запись станет финальной. Дальнейшие изменения — только через исправление с указанием причины.",
  "externalEntry.completeCorrectionConfirmBody": "Завершённое исправление сохранится как изменённая версия записи. Прежняя версия останется в истории.",
  "externalEntry.amendBody": "Выберите причину. Запись откроется для изменения с тем же содержимым. Завершённая версия останется в истории, а завершение исправления сохранит изменённую версию.",
  "externalEntry.mergedReadOnly": "Пациент объединён с другой картой. Запись доступна только для чтения.",
  "externalEntry.deskOnly": "Здесь запись доступна только для чтения. Редактирование, завершение и исправление — на компьютере.",
  "externalEntry.navDescription": "Сведения о помощи, протокол и диагнозы",
  "externalEntry.navProposed": "ИИ: предложенная запись",
  "externalEntry.navFiles": "Файлы",
  "externalEntry.navFilesDescription": "Документы, по которым внесена запись",
  "externalEntry.printUnavailable": "Печать внешней записи пока недоступна",
  "externalEntry.namesFailed": "Не удалось загрузить справочник имён. Новое имя можно ввести вручную.",
  "externalEntry.loading": "Загрузка…",
  "externalEntry.filesFailed": "Не удалось загрузить файлы пациента.",
  "externalEntry.reload": "Обновить",
  "externalEntry.reloadFailed": "Обновить не удалось. Ваш черновик сохранён в форме.",
  "externalEntry.reloaded": "Сохранённая запись загружена. В форме отображаются её текущие значения.",
  "externalEntry.history": "История записи",
  "externalEntry.historyFailed": "Не удалось загрузить историю записи.",
  "externalEntry.earlier": "Более ранние редакции",
  "externalEntry.versionNotice": "Показана редакция {revision}, сохранённая {date}.",
  "externalEntry.versionTranslations": "Переводы:",
  "externalEntry.backToCurrent": "Вернуться к текущей записи",
  "externalEntry.aiTitle": "Внешние записи из документов",
  "externalEntry.sourceWarnings": "В источнике есть неоднозначности. Сверьте запись с документами.",
  "externalEntry.summaryGroup": "Внешние записи ЭМК",
  "externalEntry.fileUnavailable": "Связанный файл недоступен.",
  "externalEntry.recognition.included": "Будут прочитаны",
  "externalEntry.recognition.excluded": "Не войдут",
  "externalEntry.recognition.reasonUnlinked": "Ещё не зарегистрированы как документы пациента.",
  "externalEntry.recognition.reasonBusy": "Уже входят в незавершённую обработку.",
  "externalEntry.recognition.openProcessing": "Открыть обработку",
  "externalEntry.recognition.reasonUnreadable": "Их текст прочитать нельзя.",
  "externalEntry.recognition.reasonOverLimit": "Одна обработка читает не больше {limit} документов.",
  "externalEntry.recognition.needsOcr": "Сначала нужно распознать текст: {count}",
  "externalEntry.recognition.ocrRefused": "Этим документам нужно распознавание текста, а запустить его вы не можете: для этого нужны право на обработку документов и включённая функция распознавания текста.",
  "externalEntry.recognition.pages": "Страниц: {pages} из {limit} возможных",
  "externalEntry.recognition.pagesUnknown": "Документов с ещё неизвестным числом страниц: {count}",
  "externalEntry.recognition.pagesKnown": "Известно страниц: {pages} из {limit} возможных",
  "externalEntry.recognition.pagesAfterOcr": "Число страниц станет известно после распознавания текста; одна обработка читает не больше {limit} страниц.",
  "externalEntry.recognition.pagesOver": "Это больше страниц, чем читает одна обработка. Уберите часть документов.",
  "externalEntry.recognition.alreadySource": "Уже источник {codes}",
  "externalEntry.recognition.alreadySourceCount": "Уже стали источником записей ЭМК: {count}",
  "externalEntry.recognition.none": "Ни один из выбранных документов прочитать нельзя.",
  "externalEntry.recognition.checking": "Проверка документов…",
  "externalEntry.recognition.checkFailed": "Не удалось проверить документы.",
  "externalEntry.recognition.checkAgain": "Проверить снова",
  "externalEntry.recognition.startFailed": "Не удалось начать обработку.",
  "externalEntry.recognition.startBusy": "Часть документов тем временем вошла в другую обработку — список показывает, где они сейчас.",
  "externalEntry.recognition.startLimit": "Документов или страниц больше, чем читает одна обработка.",
  "externalEntry.recognition.startForbidden": "У вас нет права запустить эту обработку.",
  "externalEntry.recognition.stagesLabel": "Этапы",
  "externalEntry.recognition.documentsTitle": "Документы",
  "externalEntry.recognition.ocr.notNeeded": "Текст уже был распознан",
  "externalEntry.recognition.ocr.waiting": "Ждёт распознавания текста",
  "externalEntry.recognition.ocr.running": "Текст распознаётся",
  "externalEntry.recognition.ocr.succeeded": "Текст распознан",
  "externalEntry.recognition.ocr.failed": "Текст распознать не удалось",
  "externalEntry.recognition.ocr.cancelled": "Распознавание текста отменено",
  "externalEntry.recognition.ocr.leftOut": "Пропущен",
  "externalEntry.recognition.ocrPercent": "Готово: {percent}%",
  "externalEntry.recognition.documentPages": "Страниц: {pages}",
  "externalEntry.recognition.segmented": "Размечено страниц: {done} из {total}",
  "externalEntry.recognition.events": "Найдено событий: {count}",
  "externalEntry.recognition.filled": "Заполнено записей: {done} из {total}",
  "externalEntry.recognition.executor": "Запросы к ИИ идут от имени: {name}",
  "externalEntry.recognition.attempt": "Попытка: {attempt}",
  "externalEntry.recognition.failedAt": "Остановлено на этапе: {stage}",
  "externalEntry.recognition.failedDocuments": "Документы с ошибкой",
  "externalEntry.recognition.ocrDocumentFailed": "Текст этого документа распознать не удалось.",
  "externalEntry.recognition.leftOutNote": "Документы, текст которых прочитать нельзя, пропущены — обработка читает остальные.",
  "externalEntry.recognition.retry": "Повторить",
  "externalEntry.recognition.retrying": "Повтор…",
  "externalEntry.recognition.retryFailed": "Не удалось повторить обработку.",
  "externalEntry.recognition.retryForbidden": "У вас нет права повторить эту обработку.",
  "externalEntry.recognition.error.ocrFailed": "Не удалось распознать текст части документов.",
  "externalEntry.recognition.error.documentUnreadable": "Ни в одном документе нет текста, который можно прочитать, или документ изменился после запуска обработки.",
  "externalEntry.recognition.error.pageLimitExceeded": "После распознавания текста страниц оказалось больше, чем читает одна обработка.",
  "externalEntry.recognition.error.inputTooLarge": "Текст оказался слишком большим, чтобы ИИ прочитал его за один запрос.",
  "externalEntry.recognition.error.aiFunctionDisabled": "Эта функция ИИ выключена в настройках клиники.",
  "externalEntry.recognition.error.aiKeyMissing": "У учётной записи, от имени которой идут запросы, нет ключа доступа к ИИ.",
  "externalEntry.recognition.error.accessLost": "У учётной записи, от имени которой шла обработка, больше нет доступа.",
  "externalEntry.recognition.error.promptUnavailable": "Инструкция для ИИ недоступна или настроена так, что эта обработка не может её использовать.",
  "externalEntry.recognition.error.providerRejected": "Сервис ИИ отклонил запрос.",
  "externalEntry.recognition.error.providerQuota": "У сервиса ИИ исчерпан лимит запросов или средства на счёте.",
  "externalEntry.recognition.error.providerUnavailable": "Сервис ИИ был недоступен, и автоматические повторы не помогли.",
  "externalEntry.recognition.error.retryLimitReached": "Автоматические повторы исчерпаны.",
  "externalEntry.recognition.error.callOutcomeUnknown": "Неизвестно, чем закончился запрос к ИИ: ИИ мог уже ответить, и запрос мог быть оплачен, поэтому автоматически он не повторяется. «Повторить» отправит его снова.",
  "externalEntry.recognition.error.resultInvalid": "ИИ вернул ответ, который не удалось разобрать.",
  "externalEntry.recognition.error.internalError": "Произошла внутренняя ошибка сервера.",
  "externalEntry.recognition.error.unknown": "Обработка остановилась с ошибкой.",
  "externalEntry.recognition.proposalsTitle": "Предложенные записи",
  "externalEntry.recognition.proposalsNone": "ИИ не нашёл в документах случаев оказания помощи.",
  "externalEntry.recognition.proposalsPending": "Предложенные записи появятся здесь по мере того, как ИИ их заполнит.",
  "externalEntry.recognition.proposalsStopped": "Обработка остановилась раньше, чем предложила хотя бы одну запись.",
  "externalEntry.recognition.untitled": "Без названия",
  "externalEntry.recognition.pagesRef": "стр. {pages}",
  "externalEntry.recognition.pagesRead": "Прочитано целиком страниц: {read} из {total}",
  "externalEntry.recognition.warningsCount": "Замечаний ИИ: {count}",
  "externalEntry.recognition.status.pending": "Ждёт проверки",
  "externalEntry.recognition.status.accepted": "Сохранена",
  "externalEntry.recognition.status.dismissed": "Отклонена",
  "externalEntry.recognition.savedAs": "Сохранена как {code}",
  "externalEntry.recognition.open": "Открыть",
  "externalEntry.recognition.dismiss": "Отклонить",
  "externalEntry.recognition.openNamed": "Открыть предложенную запись {name}",
  "externalEntry.recognition.dismissNamed": "Отклонить предложенную запись {name}",
  "externalEntry.recognition.dismissFailed": "Не удалось отклонить запись.",
  "externalEntry.recognition.decidedConflict": "По этой записи уже принято решение — показано её текущее состояние.",
  "externalEntry.recognition.proposalTitle": "Предложенная запись",
  "externalEntry.recognition.warningsTitle": "Замечания ИИ",
  "externalEntry.recognition.pagesPartial": "Прочитано целиком страниц: {read} из {total}. Случай оказался слишком длинным, чтобы прочитать его целиком, — сверьте запись с документами.",
  "externalEntry.recognition.decidedAccepted": "Сохранена как {code}: {name}, {at}",
  "externalEntry.recognition.decidedDismissed": "Отклонена: {name}, {at}",
  "externalEntry.recognition.cancelledReadOnly": "Обработка отменена, поэтому запись больше нельзя сохранить или отклонить.",
  "externalEntry.recognition.unsavedIntent": "Предложенная запись — изменения не сохранены",
  "externalEntry.recognition.notFound": "Предложенная запись не найдена",
  "externalEntry.recognition.notFoundDetail": "Возможно, её обработку удалили.",
  "externalEntry.recognition.loading": "Загрузка предложенной записи…",
  "externalEntry.recognition.stripTitle": "Незавершённые обработки",
  "externalEntry.recognition.fileMark": "В обработке",
  "externalEntry.recognition.awaiting": "Предложенных записей ждут проверки: {count}",
}
